import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { containsProfanity } from '../utils/profanityFilter';
import { renderMinecraftTextHTML } from '../utils/minecraftIcons';
import { signalTransitionReady } from '../components/EventTransition';
import { useAppAuth } from '../context/AuthContext';
import './LiveEventPage.css';
import './WouldYouRatherPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const DISPLAY_NAME_KEY = 'thetopia_event_displayname';

/* ── Fingerprint (lazy-loaded) ── */
let fpPromise = null;
async function getFingerprint() {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then(async (FP) => {
      const agent = await FP.load();
      const result = await agent.get();
      return result.visitorId;
    });
  }
  return fpPromise;
}
// Preload fingerprint immediately on module load
getFingerprint();

/* ── Username Gate ── */
function UsernameGate({ onSubmit }) {
  const [name, setName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || '');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 24) {
      setError('Name must be 2-24 characters');
      return;
    }
    if (!/^[a-zA-Z0-9_ -]+$/.test(trimmed)) {
      setError('Only letters, numbers, spaces, hyphens, and underscores');
      return;
    }
    if (containsProfanity(trimmed)) {
      setError('Please choose an appropriate name');
      return;
    }
    localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
    onSubmit(trimmed);
  };

  return (
    <div className="le-username-gate">
      <h2 className="le-username-title">Enter a display name</h2>
      <form onSubmit={handleSubmit} className="le-username-form">
        <input
          className="le-username-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={24}
          autoFocus
        />
        {error && <p className="le-username-error">{error}</p>}
        <button className="le-username-btn" type="submit">Join</button>
      </form>
    </div>
  );
}

/* ── Electric Border (Intensified Mode) ── */
function ElectricBorder({ color, intensity = 'normal' }) {
  const turbRef = useRef(null);
  const frameRef = useRef(null);
  const filterId = useMemo(() => `elec-${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    let t = 0;
    const speed = intensity === 'intense' ? 0.06 : 0.03;
    const animate = () => {
      t += speed;
      if (turbRef.current) {
        turbRef.current.setAttribute('seed', Math.floor(t * 5) % 200);
      }
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [intensity]);

  const strokeW = intensity === 'intense' ? 4 : 2;
  const blur = intensity === 'intense' ? 4 : 2;
  const displacement = intensity === 'intense' ? 15 : 8;

  return (
    <svg
      style={{
        position: 'absolute',
        inset: '-5px',
        width: 'calc(100% + 10px)',
        height: 'calc(100% + 10px)',
        pointerEvents: 'none',
        zIndex: 2,
        overflow: 'visible',
      }}
    >
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
          <feTurbulence ref={turbRef} type="fractalNoise" baseFrequency="0.03" numOctaves="4" seed="0" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale={displacement} xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={`${filterId}-glow`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={blur} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={`url(#${filterId}-glow)`}>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="none" stroke={color} strokeWidth={strokeW}
          filter={`url(#${filterId})`}
        />
      </g>
    </svg>
  );
}

/* ── Timer Display ── */
function WyrTimer({ state }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  if (state.roundState !== 'active' || !state.roundStartedAt) return null;

  const elapsed = (state.pausedAt ? state.pausedAt : now) - state.roundStartedAt - state.pausedElapsed;
  const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
  const secs = Math.ceil(remaining / 1000);
  const mins = Math.floor(secs / 60);
  const s = secs % 60;

  return (
    <div className="wyr-timer">
      {mins}:{s.toString().padStart(2, '0')}
    </div>
  );
}

export default function WouldYouRatherPage({ event }) {
  const slug = event?.slug;
  const { isSignedIn, minecraftUsername } = useAppAuth();
  // Cloudflare page challenge handles bot protection — connect immediately with viewer token
  const sessionToken = process.env.REACT_APP_PARTYKIT_VIEWER_TOKEN || '';

  const [displayName, setDisplayName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || '');
  const [fingerprint, setFingerprint] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [wsError, setWsError] = useState(null);
  const [lastError, setLastError] = useState(null);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const voteTimeoutRef = useRef(null);
  const [introPhase, setIntroPhase] = useState(0);
  const [logoImpact, setLogoImpact] = useState(false);
  const [voteShake, setVoteShake] = useState(false);

  // Intro animation — starts immediately on mount
  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase(1), 100); // fade in logo
    const t2 = setTimeout(() => setIntroPhase(2), 1500); // shrink and show content
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Auto-set display name from Minecraft account
  useEffect(() => {
    if (isSignedIn && minecraftUsername && displayName !== minecraftUsername) {
      localStorage.setItem(DISPLAY_NAME_KEY, minecraftUsername);
      setDisplayName(minecraftUsername);
    }
  }, [isSignedIn, minecraftUsername, displayName]);

  // Load fingerprint
  useEffect(() => {
    getFingerprint().then(setFingerprint).catch(() => {});
  }, []);

  // WebSocket connection
  const connect = useCallback(() => {
    console.log('[WYR-DEBUG] connect() called', { displayName: !!displayName, fingerprint: !!fingerprint, slug, sessionToken: sessionToken?.slice(0, 10) + '...' });
    if (!displayName || !fingerprint || !slug || !sessionToken) {
      console.log('[WYR-DEBUG] connect() blocked — missing:', { displayName: !displayName ? 'MISSING' : 'ok', fingerprint: !fingerprint ? 'MISSING' : 'ok', slug: !slug ? 'MISSING' : 'ok', sessionToken: !sessionToken ? 'MISSING' : 'ok' });
      return;
    }

    // Close any existing connection first
    clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = PARTYKIT_HOST.includes('localhost') ? 'ws' : 'wss';
    const url = `${protocol}://${PARTYKIT_HOST}/parties/wyr/${slug}`;
    console.log('[WYR-DEBUG] Connecting to:', url);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[WYR-DEBUG] WebSocket OPEN, sending join...');
      setWsError(null);
      ws.send(JSON.stringify({
        type: 'join',
        token: sessionToken,
        displayName,
        fingerprint,
      }));
      signalTransitionReady();
    };

    ws.onmessage = ({ data }) => {
      try {
        const msg = JSON.parse(data);
        console.log('[WYR-DEBUG] MSG received:', msg.type, msg.type === 'error' ? msg.message : '');

        if (msg.type === 'state') {
          console.log('[WYR-DEBUG] State:', { roundState: msg.roundState, roundNumber: msg.roundNumber, intensified: msg.intensified, question: !!msg.question, connections: msg.connections });
          setRoomState(msg);
        } else if (msg.type === 'update') {
          setRoomState(prev => prev ? {
            ...prev,
            leftVotes: msg.leftVotes,
            rightVotes: msg.rightVotes,
            leftPct: msg.leftPct,
            rightPct: msg.rightPct,
            connections: msg.connections,
          } : null);
        } else if (msg.type === 'vote-ack') {
          setRoomState(prev => prev ? {
            ...prev,
            yourVote: msg.yourVote,
            leftVotes: msg.leftVotes,
            rightVotes: msg.rightVotes,
            leftPct: msg.leftPct,
            rightPct: msg.rightPct,
          } : null);
        } else if (msg.type === 'viewers') {
          setRoomState(prev => prev ? { ...prev, connections: msg.connections } : null);
        } else if (msg.type === 'error') {
          console.error('[WYR-DEBUG] Server error:', msg.message);
          setLastError(msg.message);
          setTimeout(() => setLastError(null), 4000);
        }
      } catch (e) {
        console.error('[WYR-DEBUG] Message parse error:', e);
      }
    };

    ws.onclose = (e) => {
      console.log('[WYR-DEBUG] WebSocket CLOSED code:', e.code, 'reason:', e.reason);
      wsRef.current = null;
      reconnectRef.current = setTimeout(() => connect(), 2000);
    };

    ws.onerror = (e) => {
      console.error('[WYR-DEBUG] WebSocket ERROR:', e);
      setWsError('Connection lost. Reconnecting...');
    };
  }, [displayName, fingerprint, slug, sessionToken]);

  useEffect(() => {
    if (displayName && fingerprint && sessionToken) {
      connect();
    }
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect, displayName, fingerprint]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const handleVote = useCallback((choice) => {
    if (roomState?.yourVote === choice) return;
    sendMessage({ type: 'vote-wyr', choice, fingerprint });
    setLogoImpact(true);
    setVoteShake(true);
    if (voteTimeoutRef.current) clearTimeout(voteTimeoutRef.current);
    voteTimeoutRef.current = setTimeout(() => { setLogoImpact(false); setVoteShake(false); }, 850);
  }, [sendMessage, fingerprint, roomState?.yourVote]);

  const isActive = roomState?.roundState === 'active';
  const isWaiting = roomState?.roundState === 'waiting';
  const isIdle = !roomState || roomState.roundState === 'idle';
  const isPaused = roomState?.roundState === 'paused';
  const hasVoted = !!roomState?.yourVote;
  const intensified = roomState?.intensified || false;

  return (
    <section className={`le-page wyr-page${intensified ? ' wyr-intensified' : ''}`}>
      {/* Username gate overlay */}
      {!displayName && (
        <div className="wyr-overlay">
          <UsernameGate onSubmit={setDisplayName} />
        </div>
      )}


      <div className="wyr-layout">
        <img src={intensified ? '/img/wyr-question-marks-intensified.png' : '/img/wyr-question-marks.png'} alt="" className={`wyr-question-marks${introPhase >= 2 ? ' wyr-qm-visible' : ''}`} />
        <div className={`wyr-logo-wrap${logoImpact ? ' wyr-logo-impact' : ''}`}>
          <img
            src={intensified ? '/img/wyr-screen-intensified.png' : '/img/wyr-screen.png'}
            alt="Would You Rather?"
            className={`wyr-logo${introPhase === 1 ? ' wyr-logo-intro' : ''}${introPhase >= 2 ? ' wyr-logo-final' : ''}`}
          />
          <img
            src={intensified ? '/img/wyr-screen-intensified.png' : '/img/wyr-screen.png'}
            alt=""
            className="wyr-logo-glow"
          />
        </div>

        <div className={`wyr-content${introPhase >= 2 ? ' wyr-content-visible' : ''}`}>

        {wsError && <div className="le-ws-error">{wsError}</div>}
        {lastError && <div className="le-cmd-error">{lastError}</div>}

        {/* Status messages */}
        {isIdle && <div className="wyr-status-msg">Waiting for the game to start...</div>}
        {isPaused && <div className="wyr-status-msg">Game paused</div>}
        {isWaiting && <div className="wyr-status-msg">Waiting for next question...</div>}

        {/* Timer */}
        {isActive && <WyrTimer state={roomState} />}

        {/* Always show the two choice boxes */}
        <div className="wyr-choices">
          <div className="wyr-choice-wrap">
            <button
              className={`wyr-choice wyr-choice-left${hasVoted ? (roomState?.yourVote === 'left' ? ' wyr-choice-voted' : ' wyr-choice-other') : ''}${!isActive && !hasVoted ? ' wyr-choice-waiting' : ''}${voteShake && intensified && roomState?.yourVote === 'left' ? ' wyr-choice-shake' : ''}`}
              onClick={() => handleVote('left')}
              disabled={!isActive}
            >
              {intensified && <ElectricBorder color="#39ff73" intensity={roomState?.yourVote === 'left' ? 'intense' : 'normal'} />}
              <span className="wyr-choice-desc">
                {(intensified && roomState?.question?.leftIntensifiedDesc) || roomState?.question?.leftDesc || (isWaiting && roomState?.lastResult?.leftDesc) || '???'}
              </span>
              {(() => {
                const showActive = hasVoted && !isWaiting;
                const showWaiting = isWaiting && roomState?.lastResult;
                if (!showActive && !showWaiting) return <div className="wyr-pct-reveal" />;
                const votes = showWaiting ? roomState.lastResult.leftVotes : (roomState?.leftVotes ?? 0);
                const pct = showWaiting
                  ? (() => { const t = roomState.lastResult.leftVotes + roomState.lastResult.rightVotes; return t > 0 ? Math.round(roomState.lastResult.leftVotes / t * 100) : 0; })()
                  : (roomState?.leftPct ?? 0);
                return (
                  <div className="wyr-pct-reveal wyr-pct-visible">
                    <div className="wyr-pct-container">
                      <div className="wyr-pct-bar wyr-pct-bar-left" style={{ width: `${pct}%` }} />
                      <span className="wyr-pct-label">{pct}% ({votes} {votes === 1 ? 'vote' : 'votes'})</span>
                    </div>
                  </div>
                );
              })()}
            </button>
          </div>

          <div className="wyr-vs">OR</div>

          <div className="wyr-choice-wrap">
            <button
              className={`wyr-choice wyr-choice-right${hasVoted ? (roomState?.yourVote === 'right' ? ' wyr-choice-voted' : ' wyr-choice-other') : ''}${!isActive && !hasVoted ? ' wyr-choice-waiting' : ''}${voteShake && intensified && roomState?.yourVote === 'right' ? ' wyr-choice-shake' : ''}`}
              onClick={() => handleVote('right')}
              disabled={!isActive}
            >
              {intensified && <ElectricBorder color="#d946ef" intensity={roomState?.yourVote === 'right' ? 'intense' : 'normal'} />}
              <span className="wyr-choice-desc">
                {(intensified && roomState?.question?.rightIntensifiedDesc) || roomState?.question?.rightDesc || (isWaiting && roomState?.lastResult?.rightDesc) || '???'}
              </span>
              {(() => {
                const showActive = hasVoted && !isWaiting;
                const showWaiting = isWaiting && roomState?.lastResult;
                if (!showActive && !showWaiting) return <div className="wyr-pct-reveal" />;
                const votes = showWaiting ? roomState.lastResult.rightVotes : (roomState?.rightVotes ?? 0);
                const pct = showWaiting
                  ? (() => { const t = roomState.lastResult.leftVotes + roomState.lastResult.rightVotes; return t > 0 ? Math.round(roomState.lastResult.rightVotes / t * 100) : 0; })()
                  : (roomState?.rightPct ?? 0);
                return (
                  <div className="wyr-pct-reveal wyr-pct-visible">
                    <div className="wyr-pct-container">
                      <div className="wyr-pct-bar wyr-pct-bar-right" style={{ width: `${pct}%` }} />
                      <span className="wyr-pct-label">{pct}% ({votes} {votes === 1 ? 'vote' : 'votes'})</span>
                    </div>
                  </div>
                );
              })()}
            </button>
          </div>
        </div>

        <div className="wyr-viewers">
          {roomState?.connections || 0} watching
        </div>
        </div>
      </div>
    </section>
  );
}
